import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Which issues repeat across the most hotels?",
  "Suggest corrective actions for our weakest department",
  "Which hotel needs attention most and why?",
  "What standards should we update to prevent repeat findings?",
];

const MD_CLASS = "prose prose-sm prose-stone max-w-none prose-headings:text-[#14532D] prose-headings:font-bold prose-li:my-0.5 prose-p:my-1.5";

export default function AIInsights() {
  const { user } = useAuth();
  const [insights, setInsights] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/ai/insights/latest").then((r) => setInsights(r.data));
    api.get("/ai/chats").then((r) => {
      setMessages(r.data.flatMap((c) => [{ role: "user", text: c.question }, { role: "ai", text: c.answer }]));
    });
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/insights", {}, { timeout: 120000 });
      setInsights(data);
      toast.success("Fresh AI analysis generated");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setGenerating(false);
    }
  };

  const ask = async (q) => {
    const text = (q || question).trim();
    if (!text || streaming) return;
    setQuestion("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "ai", text: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ question: text }),
      });
      if (!res.ok) throw new Error("Query failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const obj = JSON.parse(payload);
          if (obj.error) throw new Error(obj.error);
          if (obj.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "ai", text: copy[copy.length - 1].text + obj.delta };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "AI query failed");
      setMessages((m) => (m[m.length - 1]?.text === "" ? m.slice(0, -2) : m));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="ai-insights-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-[#14532D]" strokeWidth={1.5} />AI Insights
          </h1>
          <p className="text-stone-500 mt-1 text-sm">Recurring issues across hotels, standards recommendations and plain-language analysis — powered by Claude.</p>
        </div>
        <Button data-testid="generate-insights-button" onClick={generate} disabled={generating} className="bg-[#14532D] hover:bg-[#166534]">
          <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
          {generating ? "Analyzing portfolio..." : insights ? "Regenerate Analysis" : "Generate Analysis"}
        </Button>
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="insights-panel">
        {insights ? (
          <>
            <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-3">Generated {new Date(insights.generated_at).toLocaleString()} by {insights.generated_by}</p>
            <div className={MD_CLASS}><ReactMarkdown remarkPlugins={[remarkGfm]}>{insights.content}</ReactMarkdown></div>
          </>
        ) : (
          <p className="text-sm text-stone-400 text-center py-10">{generating ? "Claude is analyzing your audit data — this takes up to a minute..." : "No analysis yet. Click Generate Analysis to identify recurring issues across your hotels."}</p>
        )}
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-hidden" data-testid="ai-chat-panel">
        <div className="px-6 py-4 border-b border-[#E7E5DF]">
          <h3 className="font-bold text-stone-900">Ask anything about your audit data</h3>
          <div className="flex flex-wrap gap-2 mt-3">
            {SUGGESTIONS.map((s) => (
              <button key={s} data-testid="ai-suggestion-chip" onClick={() => ask(s)} disabled={streaming} className="text-xs px-3 py-1.5 rounded-full border border-stone-300 text-stone-600 hover:border-[#14532D] hover:text-[#14532D] transition-colors">
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto px-6 py-4 space-y-4">
          {messages.length === 0 && <p className="text-sm text-stone-400 text-center py-6">Ask in plain language — e.g. "What should Kolhapur fix first?"</p>}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-[#14532D] text-white" : "bg-stone-50 border border-stone-200 text-stone-800"}`}>
                {m.role === "ai" ? <div className={MD_CLASS}><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text || "…"}</ReactMarkdown></div> : m.text}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <div className="px-6 py-4 border-t border-[#E7E5DF] flex gap-3">
          <Textarea
            data-testid="ai-question-input"
            rows={1}
            className="flex-1 min-h-[42px] resize-none"
            placeholder="Ask about recurring issues, scores, or corrective actions..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          />
          <Button data-testid="ai-ask-button" onClick={() => ask()} disabled={streaming || !question.trim()} className="bg-[#14532D] hover:bg-[#166534]">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
